import React, { Component } from "react";
import { CallAPI, GetCookieFunction, parseIntFromStr } from '../../utils/util';
import { PatientNav } from './patientnav';
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Alert from 'react-bootstrap/Alert';

function withRouter(Component) {
    function ComponentWithRouterProp(props) {
        let location = useLocation();
        let navigate = useNavigate();
        let params = useParams();
        return <Component {...props} router={{ location, navigate, params }} />;
    }
    return ComponentWithRouterProp;
}

class PatientSchedule extends Component {
    constructor(props) {
        super(props);
        this.patientId = parseIntFromStr(this.props.router.params.patientId);
        this.centerId = GetCookieFunction("Center").id;
        this.userType = GetCookieFunction("User").type;
        this.isAdmin = GetCookieFunction("User").admin === 1 || this.userType === "centeradmin" || this.userType === "executive";

        this.state = {
            bookings: [],
            slots: [],
            daySlots: [],
            selectedSlot: "",
            selectedDate: "",
            recurring: false,
            numWeeks: 2,
            modalVariant: "",
            modalMessage: "",
            bookingResult: null,
            confirmCancelId: null,
            loading: true
        };
    }

    componentDidMount() {
        this.loadBookings();
        // Pre-populate today's date and load slots
        const now = new Date();
        const today = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
        this.setState({ selectedDate: today });
        if (this.isAdmin) {
            this.loadDayCapacity(today);
        }
    }

    loadBookings = () => {
        CallAPI({}, `scheduling/patient/${this.patientId}`, 'GET', (res) => {
            if (res.success) {
                this.setState({ bookings: res.data, loading: false });
            } else {
                this.setState({ loading: false });
            }
        }, () => this.setState({ loading: false }));
    }

    loadDayCapacity = (date) => {
        CallAPI({}, `scheduling/day-capacity?centerId=${this.centerId}&date=${date}`, 'GET', (res) => {
            if (res.success) {
                this.setState({ daySlots: res.data });
            }
        });
    }

    handleBook = (e) => {
        e.preventDefault();
        const { selectedSlot, selectedDate, recurring, numWeeks } = this.state;

        if (!selectedSlot || !selectedDate) {
            this.setState({ modalVariant: "danger", modalMessage: "Please select a slot and date" });
            return;
        }

        const payload = {
            patientId: this.patientId,
            slotId: parseInt(selectedSlot, 10),
            startDate: selectedDate,
            recurring: recurring,
            numWeeks: recurring ? parseInt(numWeeks, 10) : 1,
            centerId: parseInt(this.centerId, 10)
        };

        CallAPI(payload, 'scheduling/book', 'POST', (res) => {
            if (res.success) {
                this.setState({
                    modalVariant: "success",
                    modalMessage: res.message,
                    bookingResult: res,
                    selectedSlot: "",
                    selectedDate: "",
                    daySlots: [],
                    recurring: false,
                    numWeeks: 2
                });
                this.loadBookings();
            } else {
                this.setState({ modalVariant: "danger", modalMessage: res.message });
            }
        }, (err) => {
            this.setState({ modalVariant: "danger", modalMessage: err.message || "Booking failed" });
        });
    }

    handleCancel = (scheduleId) => {
        this.setState({ confirmCancelId: scheduleId });
    }

    confirmCancel = () => {
        const scheduleId = this.state.confirmCancelId;
        this.setState({ confirmCancelId: null });

        CallAPI({}, `scheduling/book/${scheduleId}?centerId=${this.centerId}`, 'DELETE', (res) => {
            if (res.success) {
                this.setState({ modalVariant: "success", modalMessage: "Appointment cancelled" });
                this.loadBookings();
            } else {
                this.setState({ modalVariant: "danger", modalMessage: res.message });
            }
        }, () => {
            this.setState({ modalVariant: "danger", modalMessage: "Failed to cancel" });
        });
    }

    formatTime = (timeStr) => {
        if (!timeStr) return "";
        const [h, m] = timeStr.split(":");
        const hour = parseInt(h, 10);
        const ampm = hour >= 12 ? "PM" : "AM";
        const h12 = hour % 12 || 12;
        return `${h12}:${m} ${ampm}`;
    }

    formatDate = (dateStr) => {
        if (!dateStr) return "";
        const parts = dateStr.substring(0, 10).split('-');
        const d = new Date(parts[0], parts[1] - 1, parts[2]);
        return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    }

    render() {
        if (this.state.loading) {
            return (
                <div>
                    <PatientNav patientId={this.patientId} />
                    <div className="ci-page"><div className="ci-header"><h2>Loading...</h2></div></div>
                </div>
            );
        }

        return (
            <div>
                <PatientNav patientId={this.patientId} />
                <div className="ci-page">
                    <div className="ci-header">
                        <h2>Dialysis Schedule</h2>
                        <p>View and manage upcoming dialysis appointments</p>
                    </div>

                    {this.state.modalVariant && (
                        <Alert variant={this.state.modalVariant} className="ci-alert"
                            dismissible onClose={() => this.setState({ modalVariant: "", modalMessage: "", bookingResult: null })}>
                            {this.state.modalMessage}
                            {this.state.bookingResult && this.state.bookingResult.skipped && this.state.bookingResult.skipped.length > 0 && (
                                <ul style={{marginTop: '8px', marginBottom: 0}}>
                                    {this.state.bookingResult.skipped.map((s, i) => (
                                        <li key={i}>{this.formatDate(s.date)}: {s.reason}</li>
                                    ))}
                                </ul>
                            )}
                        </Alert>
                    )}

                    {/* Upcoming Bookings */}
                    <div className="ci-section-title">Upcoming Appointments</div>
                    <div className="ci-card">
                        {this.state.bookings.length === 0 ? (
                            <p style={{color: 'var(--text-muted)', fontSize: '14px'}}>No upcoming appointments scheduled.</p>
                        ) : (
                            <div className="editable-table-wrap">
                                <div className="editable-table-header">
                                    <div className="editable-col-main">Date</div>
                                    <div className="editable-col-main">Time Slot</div>
                                    {this.isAdmin && <div className="editable-col-action"></div>}
                                </div>
                                {this.state.bookings.map((b) => (
                                    <div key={b.scheduleId} className="editable-table-row">
                                        <div className="editable-col-main">{this.formatDate(b.scheduleDate)}</div>
                                        <div className="editable-col-main">{this.formatTime(b.startTime)} – {this.formatTime(b.endTime)}</div>
                                        {this.isAdmin && (
                                            <div className="editable-col-action">
                                                <button type="button" className="editable-delete-btn"
                                                    onClick={() => this.handleCancel(b.scheduleId)}
                                                    aria-label="Cancel">✕</button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Booking Form (admin only) */}
                    {this.isAdmin && (
                        <>
                            <div className="ci-section-title">Book New Appointment</div>
                            <div className="ci-card">
                            <div className="ci-field" style={{maxWidth: '250px'}}>
                                    <label htmlFor="selectedDate" className="ci-required-label">Select Date</label>
                                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                        <button type="button" className="funnel-nav-arrow"
                                            disabled={!this.state.selectedDate || this.state.selectedDate <= (() => { const n=new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`; })()}
                                            onClick={() => {
                                                const parts = this.state.selectedDate.split('-');
                                                const d = new Date(parts[0], parts[1]-1, parts[2]);
                                                d.setDate(d.getDate() - 1);
                                                const prev = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
                                                this.setState({ selectedDate: prev, selectedSlot: "", daySlots: [] });
                                                this.loadDayCapacity(prev);
                                            }}>←</button>
                                        <input type="date" id="selectedDate" style={{flex: 1}}
                                            min={(() => { const n=new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`; })()}
                                            value={this.state.selectedDate}
                                            onChange={(e) => {
                                                this.setState({ selectedDate: e.target.value, selectedSlot: "", daySlots: [] });
                                                if (e.target.value) this.loadDayCapacity(e.target.value);
                                            }} />
                                        <button type="button" className="funnel-nav-arrow"
                                            disabled={!this.state.selectedDate}
                                            onClick={() => {
                                                const parts = this.state.selectedDate.split('-');
                                                const d = new Date(parts[0], parts[1]-1, parts[2]);
                                                d.setDate(d.getDate() + 1);
                                                const next = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
                                                this.setState({ selectedDate: next, selectedSlot: "", daySlots: [] });
                                                this.loadDayCapacity(next);
                                            }}>→</button>
                                    </div>
                                </div>

                                {this.state.daySlots.length > 0 && (
                                    <div style={{marginTop: '16px'}}>
                                        <label style={{fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px', display: 'block'}}>Select Time Slot</label>
                                        {this.state.bookings.some(b => b.scheduleDate && b.scheduleDate.startsWith(this.state.selectedDate)) ? (
                                            <div style={{padding: '14px 20px', background: 'var(--warning-light)', borderRadius: 'var(--radius)', border: '1px solid #fde68a', fontSize: '14px', color: 'var(--text-secondary)'}}>
                                                Patient already has an appointment on this date.
                                            </div>
                                        ) : (
                                            <div className="schedule-slot-cards">
                                                {this.state.daySlots.map(slot => {
                                                    const isFull = slot.booked >= slot.availableMachines;
                                                    const remaining = slot.availableMachines - slot.booked;
                                                    const isSelected = this.state.selectedSlot === String(slot.slotId);
                                                    return (
                                                        <div key={slot.slotId}
                                                            className={`schedule-slot-card ${isFull ? 'full' : ''} ${isSelected ? 'selected' : ''}`}
                                                            onClick={() => !isFull && this.setState({ selectedSlot: String(slot.slotId) })}>
                                                            <div className="schedule-slot-time">{this.formatTime(slot.startTime)} – {this.formatTime(slot.endTime)}</div>
                                                            <div className="schedule-slot-capacity">
                                                                <span className={isFull ? 'red' : ''}>{remaining}/{slot.availableMachines}</span>
                                                                <small>{isFull ? 'Full' : 'Available'}</small>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {this.state.selectedSlot && (
                                    <form onSubmit={this.handleBook} style={{marginTop: '16px'}}>
                                        <div className="ci-checkbox-row">
                                            <label className="ci-checkbox-label">
                                                <input type="checkbox"
                                                    checked={this.state.recurring}
                                                    onChange={(e) => this.setState({ recurring: e.target.checked })} />
                                                <span>Recurring weekly</span>
                                            </label>
                                        </div>
                                        {this.state.recurring && (
                                            <div className="ci-field" style={{marginTop: '12px', maxWidth: '200px'}}>
                                                <label htmlFor="numWeeks">Number of weeks</label>
                                                <input type="number" id="numWeeks" min="2" max="52"
                                                    value={this.state.numWeeks}
                                                    onChange={(e) => this.setState({ numWeeks: e.target.value })} />
                                            </div>
                                        )}
                                        <div className="ci-submit-row" style={{marginTop: '16px'}}>
                                            <button type="submit" className="logSubmit ci-submit-btn">Book Appointment</button>
                                        </div>
                                    </form>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Cancel Confirmation Modal */}
                {this.state.confirmCancelId && (
                    <div className="ci-modal-overlay" onClick={() => this.setState({ confirmCancelId: null })}>
                        <div className="ci-modal" onClick={(e) => e.stopPropagation()}>
                            <h3>Cancel Appointment</h3>
                            <p style={{fontSize: '14px', color: 'var(--text-secondary)', margin: '12px 0'}}>
                                Are you sure you want to cancel this appointment? The slot will be released for other patients.
                            </p>
                            <div className="ci-submit-row">
                                <button type="button" className="homeButton" onClick={() => this.setState({ confirmCancelId: null })}>Keep</button>
                                <button type="button" className="logSubmit" style={{width: 'auto', background: 'var(--danger)'}} onClick={this.confirmCancel}>Cancel Appointment</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default withRouter(PatientSchedule);
